import { Box, Link, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { sendEmailVerification } from '../../utils/api';
import useLocalization from '../../hooks/useLocalization';

import { mergeSx } from '../../utils/styles';

const styles = {
  wrapper: {
    padding: 5,
  },
  header: {
    typography: 'h3',
    textAlign: 'center',
    marginBottom: 4,
  },
  paragraph: {
    color: 'neutral.100',
    typography: 'body3',
    marginBottom: 2,
    position: 'relative',
  },
  link: {
    color: 'info.main',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
  disabledLink: {
    color: 'neutral.400',
    textDecoration: 'none',
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  resend: {
    position: 'absolute',
    marginBottom: 0,
    left: '105%',
    top: '0%',
    whiteSpace: 'nowrap',
    typography: 'body3',
  },
  container: {
    position: 'relative',
  },
};

const EmailVerification = ({ email }: { email?: string }) => {
  const { t } = useLocalization();
  const [isDisabled, setIsDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const handleResendEmail = async () => {
    if (isDisabled) return;

    await sendEmailVerification(email);
    setIsDisabled(true);
    setCountdown(30);
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && isDisabled) {
      setIsDisabled(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown, isDisabled]);

  return (
    <Box sx={styles.wrapper}>
      <Typography sx={styles.header}>{t('register.emailVerificationHeader')}</Typography>
      <Typography component='p' sx={styles.paragraph}>
        {t('register.emailVerificationMessage', { email: email })}
      </Typography>
      <Typography component='p' sx={styles.paragraph}>
        {t('register.noVerificationCode')}
      </Typography>
      <Box
        sx={mergeSx(
          {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          },
          styles.container
        )}
      >
        <Typography sx={styles.paragraph}>
          <Link component='a' onClick={handleResendEmail} sx={isDisabled ? styles.disabledLink : styles.link}>
            {isDisabled ? `${t('register.clickHere')} (${countdown}${t('register.seconds')})` : t('register.clickHere')}
          </Link>{' '}
          <Typography sx={styles.resend}>{t('register.toResendEmail')}</Typography>
        </Typography>
      </Box>
    </Box>
  );
};

export default EmailVerification;
